import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon, CurrencyDollarIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';

const PaymentRefundModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  payment,
  invoice
}) => {
  const [refundType, setRefundType] = useState('refund'); // 'refund' or 'deposit'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const clinic = useSelector((state) => state.clinics.selectedClinic);
  const paymentMethods = clinic?.paymentMethods || [];

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return; // Don't submit if no reason provided
    }

    if (refundType === 'refund' && !selectedPaymentMethod) {
      return; // Don't submit if refund selected but no payment method
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        refundType,
        paymentMethod: refundType === 'refund' ? selectedPaymentMethod : null,
        reason: reason.trim()
      });
      handleClose();
    } catch (error) {
      console.error(`[Payment Refund Modal] Error processing refund:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRefundType('refund');
    setSelectedPaymentMethod('');
    setReason('');
    setIsSubmitting(false);
    onClose();
  };

  const paymentAmount = payment?.amount || 0;
  const paymentMethodDisplay = paymentMethods.find(
    method => method.value === payment?.paymentMethod
  )?.englishDescription || payment?.paymentMethod || 'Unknown Method';

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-lg my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="bg-white px-6 py-6">
            <div className="flex items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <CurrencyDollarIcon className="h-6 w-6 text-orange-600" aria-hidden="true" />
              </div>
              <div className="ml-4 mt-0 text-left flex-1">
                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Refund Payment
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    You are about to process a refund for a payment of <strong>${paymentAmount.toFixed(2)}</strong> made via <strong>{paymentMethodDisplay}</strong> for invoice <strong>{invoice?.invoiceId}</strong>.
                  </p>
                  
                  {/* Refund Type Selection */}
                  <div className="mb-4">
                    <fieldset>
                      <legend className="text-sm font-medium text-gray-700 mb-3">How would you like to process this refund?</legend>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="refund-payment"
                              name="refund-type"
                              type="radio"
                              value="refund"
                              checked={refundType === 'refund'}
                              onChange={(e) => setRefundType(e.target.value)}
                              className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300"
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="refund-payment" className="font-medium text-gray-700 flex items-center">
                              <CurrencyDollarIcon className="h-4 w-4 mr-1 text-green-600" />
                              Refund Payment
                            </label>
                            <p className="text-gray-500">Process a refund to the client's chosen payment method</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="convert-deposit"
                              name="refund-type"
                              type="radio"
                              value="deposit"
                              checked={refundType === 'deposit'}
                              onChange={(e) => setRefundType(e.target.value)}
                              className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300"
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="convert-deposit" className="font-medium text-gray-700 flex items-center">
                              <BanknotesIcon className="h-4 w-4 mr-1 text-blue-600" />
                              Convert to Deposit
                            </label>
                            <p className="text-gray-500">Keep the payment as a credit deposit for future use</p>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  {/* Payment Method Selection (only for refunds) */}
                  {refundType === 'refund' && (
                    <div className="mb-4">
                      <label htmlFor="refund-method" className="block text-sm font-medium text-gray-700 mb-2">
                        Refund Method <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="refund-method"
                        value={selectedPaymentMethod}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        disabled={isSubmitting}
                        required
                      >
                        <option value="">Select refund method...</option>
                        {paymentMethods.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.englishDescription}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Reason Input */}
                  <div className="mb-4">
                    <label htmlFor="refund-reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Reason <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="refund-reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                      placeholder={refundType === 'refund' ? "Please provide a reason for this payment refund..." : "Please provide a reason for converting this payment to a deposit..."}
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={!reason.trim() || (refundType === 'refund' && !selectedPaymentMethod) || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 
                 refundType === 'refund' ? 'Process Refund' : 'Convert to Deposit'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PaymentRefundModal;